import { useEffect, useId, useRef, useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { ISRAELI_LOCALITIES } from '@worki/shared';
import { AnchoredMenu } from './AnchoredMenu.js';
import { cn } from '../lib/cn.js';

/** Rows drawn at once. The register runs to thirteen hundred places. */
const LIMIT = 8;

interface CityInputProps extends Pick<InputHTMLAttributes<HTMLInputElement>, 'disabled' | 'aria-required' | 'aria-invalid' | 'aria-describedby'> {
  'data-document-field'?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  id?: string;
  placeholder?: string;
  'aria-label'?: string;
}

/**
 * The locality field: a text box that suggests, not a dropdown that decides.
 *
 * `Select` was the obvious reach and is the wrong shape here. It offers a
 * closed list, and this list cannot be closed — Israel has over a thousand
 * localities and a business quoting for a moshav that is not among them must
 * still be able to write its name. So this stays an `<input>`, keeps every
 * keystroke, and hangs the matches underneath it.
 *
 * Matching is on any word in the name rather than only the first, because
 * "אדומים" is how somebody looks for מעלה אדומים and "שבע" for באר שבע. The
 * popup rides the same `AnchoredMenu` as every other dropdown in the product,
 * so it portals out of whatever dialog or scroll box it was opened inside and
 * cannot be clipped by it.
 */
export function CityInput({ value, onChange, className, id, placeholder, ...aria }: CityInputProps) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [width, setWidth] = useState(0);

  const needle = value.trim();
  const matches = needle
    ? ISRAELI_LOCALITIES.filter(
        (city) => city.startsWith(needle) || city.split(/[\s-]+/).some((word) => word.startsWith(needle)),
      ).slice(0, LIMIT)
    : ISRAELI_LOCALITIES.slice(0, LIMIT);

  /* A name that is already exactly one of the suggestions has nothing left to
     suggest — the list would open on every visit to a saved record and say only
     what the field says. */
  const exact = ISRAELI_LOCALITIES.includes(needle);

  useEffect(() => setActive(0), [needle]);

  useEffect(() => {
    if (open) setWidth(wrapRef.current?.getBoundingClientRect().width ?? 0);
  }, [open, matches.length]);

  const pick = (city: string) => {
    onChange(city);
    setOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === 'Escape') {
      // Stops here: `Modal` closes on a document-level Escape, and dismissing a
      // suggestion list must not take the dialog around it down as well.
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => {
        const next = current + (event.key === 'ArrowDown' ? 1 : -1);
        return Math.min(Math.max(next, 0), matches.length - 1);
      });
      return;
    }
    if (event.key === 'Enter' && matches[active]) {
      // Only while a suggestion is highlighted, so Enter still submits the form
      // for anyone typing a locality the list does not carry.
      event.preventDefault();
      pick(matches[active]);
    }
  };

  const showList = !aria.disabled && open && matches.length > 0 && !(exact && matches.length === 1);

  return (
    <div ref={wrapRef} className="relative min-w-0">
      <input
        {...aria}
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-autocomplete="list"
        aria-label={aria['aria-label'] ?? t('customer.city')}
        placeholder={placeholder ?? t('customer.city')}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className={className}
      />

      <AnchoredMenu open={showList} onClose={() => setOpen(false)} anchorRef={wrapRef} width={Math.max(width, 180)}>
        <div id={listId} role="listbox" aria-label={t('customer.city')} className="p-1">
          {matches.map((city, index) => (
            <div
              key={city}
              role="option"
              aria-selected={city === value}
              onMouseEnter={() => setActive(index)}
              /* `onMouseDown` and not `onClick`: the input loses focus first
                 otherwise, and a blur that closes the list would take the row
                 out from under the pointer before the click landed. */
              onMouseDown={(event) => {
                event.preventDefault();
                pick(city);
              }}
              className={cn(
                'flex items-center px-2.5 min-h-11 sm:min-h-9 rounded-lg text-sm cursor-pointer',
                index === active && 'bg-muted',
                city === value && 'font-medium text-primary',
              )}
            >
              {city}
            </div>
          ))}
        </div>
      </AnchoredMenu>
    </div>
  );
}
